// CMS Passes Page
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useCMS } from './CMSContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Search, ChevronLeft, ChevronRight, Ticket, Calendar, 
  CheckCircle, XCircle, Clock, Eye, RefreshCw, Plus, Minus,
  ArrowUpCircle, KeyRound, AlertTriangle, Shield
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CMSPasses = () => {
  const { getAuthHeaders, hasRole } = useCMS();
  const [passes, setPasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [passTypeFilter, setPassTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [extendDialog, setExtendDialog] = useState({ open: false, userId: null, userName: '' });
  const [extendDays, setExtendDays] = useState(30);
  const [extendReason, setExtendReason] = useState('');
  const [importedFreeInfo, setImportedFreeInfo] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [fixingPasswords, setFixingPasswords] = useState(false);
  const [normalizing, setNormalizing] = useState(false);

  const fetchPasses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 20);
      if (search) params.append('search', search);
      if (passTypeFilter) params.append('pass_type', passTypeFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (groupFilter) params.append('group', groupFilter);

      const res = await axios.get(`${API}/cms/passes?${params.toString()}`, { 
        headers: getAuthHeaders() 
      });
      setPasses(res.data.passes);
      setTotalPages(res.data.pages);
      setTotal(res.data.total);
    } catch (err) {
      toast.error('Failed to load passes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPasses();
  }, [page, passTypeFilter, statusFilter, groupFilter]);

  // Fetch imported free user count on mount
  useEffect(() => {
    fetchImportedFreeInfo();
  }, []);

  const fetchImportedFreeInfo = async () => {
    try {
      const res = await axios.get(`${API}/cms/passes/imported-free-count`, {
        headers: getAuthHeaders()
      });
      setImportedFreeInfo(res.data);
    } catch (err) {
      // Set fallback so panel still shows with action buttons
      setImportedFreeInfo({
        imported_free_count: -1,
        password_issues: -1,
        onetime_normalize_needed: -1,
        total_annual_users: '?',
        total_one_time_users: '?',
        total_free_users: '?',
        _error: true
      });
    }
  };

  const handleUpgradeFreeToAnnual = async () => {
    if (!window.confirm(
      'Weet je zeker dat je alle geïmporteerde FREE gebruikers wilt upgraden naar Annual Pass?\n\n' +
      'Dit zal:\n' +
      '- Nieuwe GOLD codes genereren\n' +
      '- pass_type wijzigen naar "annual"\n' +
      '- Wachtwoorden repareren\n' +
      '- 1 jaar geldigheid instellen\n\n' +
      'Nieuwe registraties behouden hun FREE status.'
    )) return;

    setUpgrading(true);
    try {
      const res = await axios.post(`${API}/cms/passes/upgrade-free-to-annual`, {}, {
        headers: getAuthHeaders()
      });
      const d = res.data;
      toast.success(
        `${d.upgraded_count} gebruikers geüpgraded! ${d.codes_created} GOLD codes aangemaakt. ${d.passwords_fixed} wachtwoorden gerepareerd.`
      );
      fetchPasses();
      fetchImportedFreeInfo();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upgrade mislukt');
    } finally {
      setUpgrading(false);
    }
  };

  const handleFixPasswords = async () => {
    if (!window.confirm(
      'Wachtwoorden repareren voor alle geïmporteerde gebruikers?\n\n' +
      'Dit zorgt ervoor dat gebruikers kunnen inloggen met het wachtwoord dat ze per email hebben ontvangen.'
    )) return;

    setFixingPasswords(true);
    try {
      const res = await axios.post(`${API}/cms/passes/fix-passwords`, {}, {
        headers: getAuthHeaders()
      });
      const d = res.data;
      toast.success(`${d.fixed_count} wachtwoorden gerepareerd${d.remaining > 0 ? `. Nog ${d.remaining} over - klik opnieuw.` : ''}`);
      fetchImportedFreeInfo();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Wachtwoord fix mislukt');
    } finally {
      setFixingPasswords(false);
    }
  };

  const handleNormalizePassTypes = async () => {
    if (!window.confirm(
      'Pass types normaliseren?\n\n' +
      'Dit corrigeert "one-time" naar "one_time" in alle gebruikers en pass codes.\n' +
      'Hierdoor worden one-time passes correct weergegeven en krijgen gebruikers hun korting.'
    )) return;

    setNormalizing(true);
    try {
      const res = await axios.post(`${API}/cms/passes/normalize-pass-types`, {}, {
        headers: getAuthHeaders()
      });
      const d = res.data;
      toast.success(`${d.total_fixed} records genormaliseerd`);
      fetchPasses();
      fetchImportedFreeInfo();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Normalisatie mislukt');
    } finally {
      setNormalizing(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchPasses();
  };

  const handleActivate = async (userId, passType) => {
    try {
      await axios.post(
        `${API}/cms/passes/${userId}/activate?pass_type=${passType}&days=365`, 
        {}, 
        { headers: getAuthHeaders() }
      );
      toast.success('Pass activated');
      fetchPasses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Activation failed');
    }
  };

  const handleDeactivate = async (userId) => {
    const reason = prompt('Enter deactivation reason:');
    if (!reason) return;

    try {
      await axios.post(
        `${API}/cms/passes/${userId}/deactivate?reason=${encodeURIComponent(reason)}`, 
        {}, 
        { headers: getAuthHeaders() }
      );
      toast.success('Pass deactivated');
      fetchPasses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Deactivation failed');
    }
  };

  const handleExtend = async () => {
    if (!extendReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    try {
      await axios.post(
        `${API}/cms/passes/${extendDialog.userId}/extend`, 
        { days: extendDays, reason: extendReason },
        { headers: getAuthHeaders() }
      );
      toast.success(`Pass extended by ${extendDays} days`);
      setExtendDialog({ open: false, userId: null, userName: '' });
      setExtendDays(30);
      setExtendReason('');
      fetchPasses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Extension failed');
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500/20 text-green-400"><CheckCircle className="w-3 h-3 mr-1" />Active</Badge>;
      case 'expired':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Expired</Badge>;
      case 'no_expiry':
        return <Badge className="bg-blue-500/20 text-blue-400"><CheckCircle className="w-3 h-3 mr-1" />One-Time</Badge>;
      default:
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />{status || 'Unknown'}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Passes</h1>
          <p className="text-slate-400">{total} total passes</p>
        </div>
        <Button variant="outline" onClick={fetchPasses} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Imported Users Management Panel */}
      {hasRole('super_admin') && importedFreeInfo && (
        <Card className="bg-amber-950/20 border-amber-700/50">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-amber-400 text-lg">
              <AlertTriangle className="w-5 h-5" />
              Gebruikers Management
            </CardTitle>
            <CardDescription className="text-amber-300/70">
              {importedFreeInfo._error 
                ? 'Kon data niet ophalen (database timeout). Gebruik de knoppen hieronder om acties uit te voeren.'
                : 'Beheer geïmporteerde gebruikers, wachtwoorden en pass types'
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Stats Row - only show if data loaded successfully */}
            {!importedFreeInfo._error && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-amber-400" data-testid="imported-free-count">
                    {importedFreeInfo.imported_free_count}
                  </p>
                  <p className="text-xs text-slate-400">Geïmporteerd FREE</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-red-400" data-testid="password-issues-count">
                    {importedFreeInfo.password_issues}
                  </p>
                  <p className="text-xs text-slate-400">Wachtwoord Problemen</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-orange-400" data-testid="normalize-needed-count">
                    {importedFreeInfo.onetime_normalize_needed || 0}
                  </p>
                  <p className="text-xs text-slate-400">One-Time Fix Nodig</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-400">{importedFreeInfo.total_annual_users}</p>
                  <p className="text-xs text-slate-400">Annual Users</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-400">{importedFreeInfo.total_one_time_users || 0}</p>
                  <p className="text-xs text-slate-400">One-Time Users</p>
                </div>
              </div>
            )}

            {/* Action Buttons - always visible */}
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={handleUpgradeFreeToAnnual}
                disabled={upgrading}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="upgrade-free-to-annual-btn"
              >
                {upgrading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ArrowUpCircle className="w-4 h-4 mr-2" />
                )}
                {upgrading ? 'Bezig met upgraden...' : 'Upgrade FREE → Annual (GOLD)'}
              </Button>
              <Button
                onClick={handleFixPasswords}
                disabled={fixingPasswords}
                variant="outline"
                className="border-red-600 text-red-400 hover:bg-red-950/30"
                data-testid="fix-passwords-btn"
              >
                {fixingPasswords ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <KeyRound className="w-4 h-4 mr-2" />
                )}
                {fixingPasswords ? 'Bezig met repareren...' : 'Fix Wachtwoorden'}
              </Button>
              <Button
                onClick={handleNormalizePassTypes}
                disabled={normalizing}
                variant="outline"
                className="border-orange-600 text-orange-400 hover:bg-orange-950/30"
                data-testid="normalize-pass-types-btn"
              >
                {normalizing ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Shield className="w-4 h-4 mr-2" />
                )}
                {normalizing ? 'Bezig...' : 'Normaliseer One-Time Records'}
              </Button>
              {importedFreeInfo._error && (
                <Button
                  onClick={fetchImportedFreeInfo}
                  variant="outline"
                  className="border-slate-600 text-slate-400"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Herlaad Stats
                </Button>
              )}
            </div>

            <p className="text-xs text-slate-500">
              Upgrade: FREE → Annual met GOLD codes (1 jaar). Wachtwoorden: fix hash zodat login werkt.
              Normaliseer: corrigeer "one-time" → "one_time" zodat korting correct wordt weergegeven.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  placeholder="Search by email or pass code..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 bg-slate-900 border-slate-600"
                  data-testid="pass-search-input"
                />
              </div>
            </div>
            <Select value={passTypeFilter || "all"} onValueChange={(v) => setPassTypeFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40 bg-slate-900 border-slate-600" data-testid="pass-type-filter">
                <SelectValue placeholder="Pass Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="one_time">One-Time</SelectItem>
                <SelectItem value="annual">Annual</SelectItem>
                <SelectItem value="b2b">B2B</SelectItem>
              </SelectContent>
            </Select>
            <Select value={groupFilter || "all"} onValueChange={(v) => setGroupFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40 bg-slate-900 border-slate-600" data-testid="pass-group-filter">
                <SelectValue placeholder="Group" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Groups</SelectItem>
                <SelectItem value="users">Users</SelectItem>
                <SelectItem value="b2b">B2B</SelectItem>
                <SelectItem value="managers">Managers</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40 bg-slate-900 border-slate-600" data-testid="pass-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" data-testid="pass-search-btn">Search</Button>
          </form>
        </CardContent>
      </Card>

      {/* Passes Table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-slate-700 bg-slate-900/50">
                <tr>
                  <th className="text-left p-4 text-slate-400 font-medium">User</th>
                  <th className="text-left p-4 text-slate-400 font-medium">Pass Code</th>
                  <th className="text-left p-4 text-slate-400 font-medium">Pass Type</th>
                  <th className="text-left p-4 text-slate-400 font-medium">Status</th>
                  <th className="text-left p-4 text-slate-400 font-medium">Activated</th>
                  <th className="text-left p-4 text-slate-400 font-medium">Expires</th>
                  <th className="text-right p-4 text-slate-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-slate-400">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : passes.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-slate-400">
                      No passes found
                    </td>
                  </tr>
                ) : (
                  passes.map((pass) => (
                    <tr key={pass.user_id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="p-4">
                        <div>
                          <p className="font-medium">{pass.first_name || pass.name || ''} {pass.last_name || ''}</p>
                          <p className="text-sm text-slate-400">{pass.email}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <code className={`text-xs px-2 py-1 rounded ${
                          pass.pass_code?.startsWith('GOLD') ? 'bg-amber-900/30 text-amber-400' :
                          pass.pass_code?.startsWith('FREE') ? 'bg-slate-700 text-slate-400' :
                          pass.pass_code?.startsWith('ST-') ? 'bg-blue-900/30 text-blue-400' :
                          pass.pass_code?.startsWith('NL-') ? 'bg-green-900/30 text-green-400' :
                          'bg-slate-700 text-slate-300'
                        }`} data-testid={`pass-code-${pass.user_id}`}>
                          {pass.pass_code || 'Geen code'}
                        </code>
                      </td>
                      <td className="p-4">
                        <Badge variant={pass.pass_type === 'annual' ? 'default' : 'secondary'}>
                          <Ticket className="w-3 h-3 mr-1" />
                          {pass.pass_type || 'free'}
                        </Badge>
                      </td>
                      <td className="p-4">
                        {getStatusBadge(pass.pass_status)}
                      </td>
                      <td className="p-4 text-slate-400">
                        {formatDate(pass.pass_activated_at)}
                      </td>
                      <td className="p-4 text-slate-400">
                        {formatDate(pass.pass_expiry || pass.pass_expires_at)}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <Link to={`/cms/users/${pass.user_id}`}>
                            <Button variant="ghost" size="sm" data-testid={`view-user-${pass.user_id}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                          {hasRole('super_admin', 'support') && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setExtendDialog({ 
                                  open: true, 
                                  userId: pass.user_id, 
                                  userName: `${pass.first_name || pass.name || ''} ${pass.last_name || ''}`.trim()
                                })}
                                disabled={pass.pass_status !== 'active'}
                              >
                                <Plus className="w-4 h-4 text-green-400" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeactivate(pass.user_id)}
                              >
                                <Minus className="w-4 h-4 text-red-400" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-slate-700">
              <p className="text-sm text-slate-400">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Extend Dialog */}
      <Dialog open={extendDialog.open} onOpenChange={(open) => setExtendDialog({ ...extendDialog, open })}>
        <DialogContent className="bg-white border-gray-200 text-gray-900">
          <DialogHeader>
            <DialogTitle>Extend Pass for {extendDialog.userName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Days to Extend</Label>
              <Input
                type="number"
                value={extendDays}
                onChange={(e) => setExtendDays(parseInt(e.target.value) || 0)}
                min={1}
                max={365}
                className="bg-slate-900 border-slate-600"
              />
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea
                value={extendReason}
                onChange={(e) => setExtendReason(e.target.value)}
                placeholder="Enter reason for extension..."
                className="bg-slate-900 border-slate-600"
              />
            </div>
            <Button onClick={handleExtend} className="w-full">
              Extend Pass
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMSPasses;
